import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Plus, Trash2, Rss, RefreshCw, Clock, FileText, ExternalLink, AlertCircle, 
  Eye, CheckCircle, X, Loader2, Image as ImageIcon, Calendar, Activity
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const RSSTab = () => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [fetching, setFetching] = useState({});
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: "",
    url: "",
    auto_publish: true,
    category: ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    fetchFeeds();
    fetchSchedulerStatus();
  }, []);

  const fetchFeeds = async () => {
    try {
      const res = await axios.get(`${API}/news/rss/feeds`);
      setFeeds(res.data);
    } catch (err) {
      console.error("Error fetching feeds:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const res = await axios.get(`${API}/news/admin/rss/scheduler/status`, { headers: getAuthHeader() });
      setSchedulerStatus(res.data);
    } catch (err) {
      console.error("Error fetching scheduler status:", err);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    try {
      await axios.post(`${API}/news/admin/rss/scheduler/fetch-now`, {}, { headers: getAuthHeader() });
      alert("RSS fetch triggered! Check back in a moment for new articles.");
      fetchFeeds();
      fetchSchedulerStatus();
    } catch (err) {
      alert("Error refreshing feeds: " + (err.response?.data?.detail || err.message));
    } finally {
      setRefreshingAll(false);
    }
  };

  // Preview RSS feed before adding
  const handlePreview = async () => {
    if (!form.url) {
      setError("Please enter a feed URL");
      return;
    }

    setPreviewLoading(true);
    setPreviewError("");
    setError("");

    try {
      const res = await axios.post(`${API}/news/rss/preview`, { url: form.url });
      setPreviewData(res.data);
      setShowPreview(true);
      
      // Auto-fill name from feed title if empty
      if (!form.name && res.data.feed?.title) {
        setForm({ ...form, name: res.data.feed.title });
      }
    } catch (err) {
      setPreviewError(err.response?.data?.detail || "Failed to preview feed. Please check the URL.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Confirm and add feed after preview
  const handleConfirmAdd = async () => {
    setSaving(true);
    setError("");

    try {
      const res = await axios.post(`${API}/news/rss/add`, form);
      fetchFeeds();
      setShowAdd(false);
      setShowPreview(false);
      setPreviewData(null);
      setForm({ name: "", url: "", auto_publish: true, category: "" });
      if (res.data.articles_imported > 0) {
        alert(`Feed added! Imported ${res.data.articles_imported} articles.`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add feed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (feedId) => {
    if (!window.confirm("Delete this news feed? Articles already imported will remain.")) return;

    try {
      await axios.delete(`${API}/news/rss/${feedId}`);
      fetchFeeds();
    } catch (err) {
      console.error("Error deleting feed:", err);
    }
  };

  const handleFetch = async (feedId) => {
    setFetching({ ...fetching, [feedId]: true });

    try {
      const res = await axios.post(`${API}/news/rss/fetch/${feedId}`);
      alert(`Imported ${res.data.articles_added} new articles`);
      fetchFeeds();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to fetch feed");
    } finally {
      setFetching({ ...fetching, [feedId]: false });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  const closeModal = () => {
    setShowAdd(false);
    setShowPreview(false);
    setPreviewData(null);
    setPreviewError("");
    setError("");
    setForm({ name: "", url: "", auto_publish: true, category: "" });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white">
            News Feeds
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Import articles automatically from news feeds
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleRefreshAll}
            disabled={refreshingAll}
            className="bg-zinc-800 text-white hover:bg-zinc-700"
            data-testid="refresh-all-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshingAll ? 'animate-spin' : ''}`} />
            {refreshingAll ? "Refreshing..." : "Refresh All"}
          </Button>
          <Button
            onClick={() => setShowAdd(true)}
            className="bg-white text-black hover:bg-zinc-200"
            data-testid="add-feed-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Feed
          </Button>
        </div>
      </div>

      {/* Scheduler Status Banner */}
      <div className="bg-zinc-800/50 border border-white/10 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className={`w-5 h-5 ${schedulerStatus?.running ? 'text-green-500' : 'text-red-500'}`} />
            <div>
              <p className="text-white font-medium text-sm">
                RSS Scheduler {schedulerStatus?.running ? 'Running' : 'Stopped'}
              </p>
              <p className="text-zinc-500 text-xs">
                Feeds refresh every <span className="text-orange-400 font-medium">hour</span> • Articles auto-delete after <span className="text-orange-400 font-medium">24 hours</span>
              </p>
            </div>
          </div>
          {schedulerStatus?.jobs && (
            <div className="text-right text-xs">
              {schedulerStatus.jobs.map((job, idx) => (
                <p key={idx} className="text-zinc-400">
                  <span className="text-white">{job.name.split(' ')[0]}</span>: {job.next_run ? new Date(job.next_run).toLocaleTimeString() : 'N/A'}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Feed Modal */}
      {showAdd && !showPreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-white/10 w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold uppercase text-white">
                Add News Feed
              </h3>
              <button onClick={closeModal} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {(error || previewError) && (
                <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <span className="text-red-500 text-sm">{error || previewError}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  Feed URL *
                </label>
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://example.com/feed.xml"
                  required
                  className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
                  data-testid="rss-url-input"
                />
                <p className="text-xs text-zinc-600 mt-1">Enter a news feed URL (RSS or Atom format)</p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  Feed Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., ESPN College Football (auto-fills from preview)"
                  className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
                  data-testid="rss-name-input"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Sports News"
                  className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="auto_publish"
                  checked={form.auto_publish}
                  onChange={(e) => setForm({ ...form, auto_publish: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="auto_publish" className="text-zinc-400 text-sm">
                  Auto-publish imported articles
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handlePreview}
                  disabled={previewLoading || !form.url}
                  className="flex-1 bg-orange-500 text-white hover:bg-orange-600"
                  data-testid="preview-feed-btn"
                >
                  {previewLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Preview Feed
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-white/10 w-full max-w-2xl my-8"
          >
            {/* Preview Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {previewData.feed.image ? (
                    <img 
                      src={previewData.feed.image} 
                      alt="" 
                      className="w-12 h-12 object-contain bg-zinc-800 rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-orange-500/20 flex items-center justify-center rounded">
                      <Rss className="w-6 h-6 text-orange-500" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white">
                      {previewData.feed.title || "News Feed"}
                    </h3>
                    <p className="text-zinc-500 text-sm">
                      {previewData.total_count} articles available
                    </p>
                  </div>
                </div>
                <button onClick={closeModal} className="text-zinc-500 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {previewData.feed.description && (
                <p className="text-zinc-400 text-sm mt-3 line-clamp-2">
                  {previewData.feed.description}
                </p>
              )}
            </div>

            {/* Articles Preview */}
            <div className="p-6 max-h-[400px] overflow-y-auto">
              <h4 className="text-xs font-bold uppercase text-zinc-400 mb-4">
                Preview Articles (First 10)
              </h4>
              
              <div className="space-y-3">
                {previewData.articles.map((article, index) => (
                  <div 
                    key={index}
                    className="flex gap-3 p-3 bg-zinc-800/50 border border-white/5 hover:bg-zinc-800"
                  >
                    {article.image ? (
                      <img 
                        src={article.image} 
                        alt="" 
                        className="w-16 h-16 object-cover flex-shrink-0 bg-zinc-700"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-zinc-700 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-6 h-6 text-zinc-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h5 className="text-white text-sm font-medium line-clamp-2">
                        {article.title}
                      </h5>
                      {article.summary && (
                        <p className="text-zinc-500 text-xs mt-1 line-clamp-1">
                          {article.summary.replace(/<[^>]*>/g, '')}
                        </p>
                      )}
                      {article.published && (
                        <p className="text-zinc-600 text-[10px] mt-1">
                          {article.published}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Confirm Actions */}
            <div className="p-6 border-t border-white/10 bg-zinc-900/50">
              {error && (
                <div className="flex items-center gap-3 p-3 mb-4 bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-500 text-sm">{error}</span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-400">
                  <span className="text-white font-medium">{form.name || previewData.feed.title}</span>
                  <span className="text-zinc-500 ml-2">• {form.auto_publish ? "Auto-publish" : "Manual review"}</span>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowPreview(false);
                      setPreviewData(null);
                    }}
                    className="bg-zinc-800 text-white hover:bg-zinc-700"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleConfirmAdd}
                    disabled={saving}
                    className="bg-green-600 text-white hover:bg-green-700"
                    data-testid="confirm-add-feed-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirm & Import
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Feeds List */}
      {feeds.length === 0 ? (
        <div className="bg-zinc-900/50 border border-white/10 p-12 text-center">
          <Rss className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-white font-bold mb-2">No News Feeds</h3>
          <p className="text-zinc-500 mb-6">
            Add news feeds to automatically import articles from external sources.
          </p>
          <Button
            onClick={() => setShowAdd(true)}
            className="bg-zinc-800 text-white hover:bg-zinc-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Feed
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {feeds.map((feed) => (
            <div
              key={feed.id}
              className="bg-zinc-900/50 border border-white/10 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/20 flex items-center justify-center">
                    <Rss className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{feed.name}</h3>
                    <a
                      href={feed.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-500 text-sm flex items-center gap-1 hover:text-white"
                    >
                      {feed.url.length > 50 ? feed.url.substring(0, 50) + "..." : feed.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleFetch(feed.id)}
                    disabled={fetching[feed.id]}
                    className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${fetching[feed.id] ? "animate-spin" : ""}`} />
                    {fetching[feed.id] ? "Fetching..." : "Fetch Now"}
                  </Button>
                  <button
                    onClick={() => handleDelete(feed.id)}
                    className="p-2 text-zinc-500 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
                <div>
                  <p className="text-zinc-500 text-xs uppercase">Category</p>
                  <p className="text-white">{feed.category}</p>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase">Articles Imported</p>
                  <p className="text-white flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {feed.article_count || 0}
                  </p>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase">Last Fetched</p>
                  <p className="text-white flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(feed.last_fetched)}
                  </p>
                </div>
              </div>

              <div className="mt-3">
                <span className={`text-xs px-2 py-1 ${
                  feed.auto_publish
                    ? "bg-green-500/20 text-green-500"
                    : "bg-zinc-500/20 text-zinc-400"
                }`}>
                  {feed.auto_publish ? "Auto-Publish Enabled" : "Manual Review"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default RSSTab;
